import { useState, useEffect, useCallback } from 'react';
import { whatsappApi } from '../../../services/api';
import { toast } from 'react-hot-toast';
import { Loader2, ChevronDown, ChevronUp, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';

export default function HistoryTab() {
  usePageTitle('Histórico');
  const [loading, setLoading] = useState(true);
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [expandedDispatch, setExpandedDispatch] = useState<number | null>(null);
  const [dispatchItems, setDispatchItems] = useState<Record<number, any[]>>({});
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  const loadDispatches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await whatsappApi.listDispatches(page, 10);
      setDispatches(response.dispatches || []);
      setPagination(response.pagination);
    } catch (error: any) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadDispatches();
  }, [page, loadDispatches]);


  const loadDispatchItems = async (dispatchId: number) => {
    try {
      const response = await whatsappApi.getDispatchItems(dispatchId, 1, 50);
      setDispatchItems(prev => ({ ...prev, [dispatchId]: response.items }));
    } catch (error: any) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const toggleExpand = (dispatchId: number) => {
    if (expandedDispatch === dispatchId) {
      setExpandedDispatch(null);
    } else {
      setExpandedDispatch(dispatchId);
      if (!dispatchItems[dispatchId]) {
        loadDispatchItems(dispatchId);
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { icon: any; class: string; label: string }> = {
      pending: { icon: Clock, class: 'bg-gray-100 text-gray-800', label: 'Pendente' },
      processing: { icon: Loader2, class: 'bg-blue-100 text-blue-800', label: 'Processando' },
      completed: { icon: CheckCircle, class: 'bg-green-100 text-green-800', label: 'Concluído' },
      failed: { icon: XCircle, class: 'bg-red-100 text-red-800', label: 'Falhou' },
      cancelled: { icon: AlertCircle, class: 'bg-yellow-100 text-yellow-800', label: 'Cancelado' }
    };

    const config = configs[status] || configs.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.class}`}>
        <Icon className={`h-4 w-4 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Histórico de Disparos</h2>

      {dispatches.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>Nenhum disparo realizado ainda</p>
        </div>
      ) : (
        <div className="space-y-4">
          {dispatches.map((dispatch) => (
            <div key={dispatch.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Header */}
              <div
                onClick={() => toggleExpand(dispatch.id)}
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-lg">{dispatch.template_name}</h3>
                      {getStatusBadge(dispatch.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Total</p>
                        <p className="font-semibold">{dispatch.total_recipients}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Enviados</p>
                        <p className="font-semibold text-green-600">{dispatch.sent_count}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Falhas</p>
                        <p className="font-semibold text-red-600">{dispatch.failed_count}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Data</p>
                        <p className="font-semibold">{formatDate(dispatch.created_at)}</p>
                      </div>
                    </div>

                    {dispatch.created_by_name && (
                      <p className="text-xs text-gray-500 mt-2">
                        Criado por: {dispatch.created_by_name}
                      </p>
                    )}
                  </div>

                  <div className="ml-4">
                    {expandedDispatch === dispatch.id ? (
                      <ChevronUp className="h-6 w-6 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Items */}
              {expandedDispatch === dispatch.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <h4 className="font-medium mb-3">Detalhes do Disparo:</h4>
                  
                  {!dispatchItems[dispatch.id] ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">WhatsApp</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enviado em</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {dispatchItems[dispatch.id].map((item: any) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm">{item.lead_name}</td>
                              <td className="px-4 py-2 text-sm font-mono">{item.phone}</td>
                              <td className="px-4 py-2 text-sm">
                                {item.status === 'sent' && <span className="text-green-600">✓ Enviado</span>}
                                {item.status === 'pending' && <span className="text-gray-600">Pendente</span>}
                                {item.status === 'sending' && <span className="text-blue-600">Enviando...</span>}
                                {item.status === 'failed' && <span className="text-red-600">✗ Falhou</span>}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {item.sent_at ? formatDate(item.sent_at) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-lg shadow p-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 bg-primary-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          
          <span className="text-sm text-gray-600">
            Página {page} de {pagination.totalPages}
          </span>
          
          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 bg-primary-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}
